import './marketing.css';
import './gallery.css';
import { LOCAL_TEMPLATE_HANDOFF_KEY } from './app/routes';
import { el } from './ui/dom';
import { renderMarkdown } from './ui/markdown';

interface Publication {
  id: string;
  name: string;
  publisherHandle: string;
  revision: number;
  bodyHash: string;
  body?: string;
  artistMarkdown?: string;
  canEditArtistMarkdown?: boolean;
  license: { identifier: 'CC0-1.0'; url: string };
  publishedAt: number;
  updatedAt: number;
  upvotes: number;
  previewUrl: string | null;
  previewVersion: number;
}

type LivePreview = {
  load(body: string): void;
  dispose(): void;
};

const rootNode = document.getElementById('gallery-app');
if (!rootNode) throw new Error('Missing #gallery-app');
const root: HTMLElement = rootNode;

const navToggle = document.querySelector<HTMLButtonElement>('[data-nav-toggle]');
const siteLinks = document.querySelector<HTMLElement>('[data-site-links]');
const siteHeader = document.querySelector<HTMLElement>('[data-header]');
const setNavigation = (open: boolean): void => {
  if (!navToggle || !siteLinks) return;
  navToggle.setAttribute('aria-expanded', String(open));
  navToggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
  siteLinks.classList.toggle('open', open);
};
navToggle?.addEventListener('click', () =>
  setNavigation(navToggle.getAttribute('aria-expanded') !== 'true'),
);
siteLinks?.addEventListener('click', () => setNavigation(false));
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') setNavigation(false);
});
const updateHeader = (): void => {
  siteHeader?.classList.toggle('scrolled', window.scrollY > 18);
};
updateHeader();
window.addEventListener('scroll', updateHeader, { passive: true });
const year = document.querySelector<HTMLElement>('[data-year]');
if (year) year.textContent = String(new Date().getFullYear());

let sort: 'top' | 'new' = 'top';
let items: Publication[] = [];
let nextCursor: string | null = null;
let loading = true;
let error = '';
let detailPreview: LivePreview | null = null;

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error ?? `Request failed (${response.status})`);
  return payload;
};

const relativeTime = (milliseconds: number): string => {
  const days = Math.max(0, Math.round((Date.now() - milliseconds) / 86_400_000));
  if (days === 0) return 'Today';
  if (days < 30) return `${days}d ago`;
  return new Date(milliseconds).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const upvoteIcon = (filled = false): SVGElement => {
  const namespace = 'http://www.w3.org/2000/svg';
  const icon = document.createElementNS(namespace, 'svg');
  icon.setAttribute('class', `upvote-icon${filled ? ' filled' : ''}`);
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');
  const arrow = document.createElementNS(namespace, 'path');
  arrow.setAttribute('d', 'M12 3.25 4.75 11h4v8.25h6.5V11h4L12 3.25Z');
  const notch = document.createElementNS(namespace, 'path');
  notch.setAttribute('class', 'upvote-notch');
  notch.setAttribute('d', 'M9 11h6');
  icon.append(arrow, notch);
  return icon;
};

const staticPreview = (publication: Publication): HTMLElement =>
  el('div', { class: 'gallery-preview' }, [
    publication.previewUrl
      ? el('img', { src: publication.previewUrl, alt: '', loading: 'lazy' })
      : el('span', { class: 'gallery-placeholder', 'aria-hidden': 'true' }, [
          publication.name.slice(0, 1).toUpperCase() || 'K',
        ]),
  ]);

const card = (publication: Publication): HTMLElement =>
  el('a', { class: 'gallery-card', href: `/gallery/${publication.id}` }, [
    staticPreview(publication),
    el('div', { class: 'gallery-card-copy' }, [
      el('strong', {}, [publication.name]),
      el('div', { class: 'gallery-meta' }, [
        el('span', {}, [`@${publication.publisherHandle}`]),
        el('span', { class: 'gallery-vote-count' }, [
          upvoteIcon(false),
          String(publication.upvotes),
          el('span', { class: 'gallery-sr-only' }, [
            publication.upvotes === 1 ? ' upvote' : ' upvotes',
          ]),
        ]),
        el('span', {}, [relativeTime(publication.publishedAt)]),
        el('span', { class: 'cc0' }, ['CC0']),
      ]),
    ]),
  ]);

async function load(reset = false): Promise<void> {
  if (reset) {
    items = [];
    nextCursor = null;
  }
  loading = true;
  error = '';
  render();
  try {
    const query = new URLSearchParams({ sort, limit: '24' });
    if (!reset && nextCursor) query.set('cursor', nextCursor);
    const result = await request<{ publications: Publication[]; nextCursor: string | null }>(
      `/gallery?${query}`,
    );
    items = reset ? result.publications : [...items, ...result.publications];
    nextCursor = result.nextCursor;
  } catch (reason) {
    error = (reason as Error).message;
  } finally {
    loading = false;
    render();
  }
}

const renderPreviewFailure = (
  host: HTMLElement,
  publication: Publication,
  reason: string,
): void => {
  host.replaceChildren(
    publication.previewUrl
      ? el('img', { src: publication.previewUrl, alt: '' })
      : el('span', { class: 'gallery-placeholder', 'aria-hidden': 'true' }, [
          publication.name.slice(0, 1).toUpperCase() || 'K',
        ]),
    el('span', { class: 'gallery-render-status', role: 'status' }, [reason]),
  );
};

const createLivePreview = async (
  host: HTMLElement,
  publication: Publication,
): Promise<LivePreview> => {
  if (!publication.body) throw new Error('This revision has no renderable graph');
  const [
    { Store },
    { RustWasmRendererRuntime },
    { Preview3D },
    { attachPreview3DControls },
    { registerBuiltinNodes },
  ] = await Promise.all([
    import('./app/store'),
    import('./renderer/wasm'),
    import('./ui/preview3d'),
    import('./ui/preview3d-controls'),
    import('./nodes'),
  ]);
  registerBuiltinNodes();
  const runtime = await RustWasmRendererRuntime.create();
  const store = new Store(runtime.createEvaluator());
  host.replaceChildren();
  const preview = new Preview3D(host, runtime, store);
  const loadBody = (body: string): void => {
    host.setAttribute('aria-busy', 'true');
    store.loadDocument(body);
    const result = store.evaluateNow();
    const renderedChannels = [...store.graph.outputs()]
      .filter(([, node]) => result.nodes.has(node.id))
      .map(([channel]) => channel);
    if (!renderedChannels.length) {
      const message = result.errors.values().next().value ?? 'The graph has no rendered outputs';
      throw new Error(message);
    }
    host.dataset.renderedChannels = renderedChannels.join(',');
    host.removeAttribute('aria-busy');
  };
  const removeControls = attachPreview3DControls(host, preview);
  try {
    loadBody(publication.body);
  } catch (error) {
    removeControls();
    preview.dispose();
    store.evaluator.dispose();
    runtime.dispose();
    throw error;
  }
  return {
    load: loadBody,
    dispose: () => {
      removeControls();
      preview.dispose();
      store.evaluator.dispose();
      runtime.dispose();
    },
  };
};

const artistNotes = (publication: Publication): HTMLElement => {
  const section = el('section', {
    class: 'gallery-artist-notes',
    'aria-labelledby': 'artist-notes-title',
  });
  const heading = el('h2', { id: 'artist-notes-title' }, ['About this material']);
  const body = el('div', { class: 'gallery-markdown' });
  const controls = el('div', { class: 'gallery-note-controls' });
  let markdown = publication.artistMarkdown ?? '';

  const showNotes = (): void => {
    controls.replaceChildren();
    body.replaceChildren();
    if (markdown.trim()) body.appendChild(renderMarkdown(markdown));
    else
      body.appendChild(
        el('p', { class: 'gallery-muted' }, ['The artist has not added notes yet.']),
      );
    if (publication.canEditArtistMarkdown) {
      controls.appendChild(
        el('button', { class: 'gallery-text-button', type: 'button', onclick: showEditor }, [
          markdown.trim() ? 'Edit notes' : 'Add notes',
        ]),
      );
    }
  };

  const showEditor = (): void => {
    const textarea = el('textarea', {
      class: 'gallery-markdown-editor',
      rows: 10,
      'aria-label': 'Artist notes in Markdown',
      placeholder:
        'Describe the material, useful parameters, intended scale, or rendering tips. Markdown is supported.',
    }) as HTMLTextAreaElement;
    textarea.value = markdown;
    const counter = el('span', { class: 'gallery-character-count', 'aria-live': 'polite' });
    const save = el('button', { class: 'gallery-button primary', type: 'button' }, [
      'Save notes',
    ]) as HTMLButtonElement;
    const updateCounter = (): void => {
      const count = [...textarea.value].length;
      counter.textContent = `${count.toLocaleString()} / 4,000`;
      save.disabled = count > 4000;
    };
    const saveNotes = async (): Promise<void> => {
      save.disabled = true;
      try {
        const result = await request<{ artistMarkdown: string }>(
          `/publications/${encodeURIComponent(publication.id)}`,
          { method: 'PATCH', body: JSON.stringify({ artistMarkdown: textarea.value }) },
        );
        markdown = result.artistMarkdown;
        publication.artistMarkdown = markdown;
        showNotes();
      } catch (reason) {
        window.alert((reason as Error).message);
        updateCounter();
      }
    };
    save.onclick = () => void saveNotes();
    textarea.addEventListener('input', updateCounter);
    textarea.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && !save.disabled) {
        event.preventDefault();
        void saveNotes();
      }
    });
    body.replaceChildren(
      el('p', { class: 'gallery-note-help' }, [
        'Markdown supports headings, lists, quotes, links, emphasis, and code. Artist notes are mutable listing text.',
      ]),
      textarea,
    );
    controls.replaceChildren(
      counter,
      el('button', { class: 'gallery-button', type: 'button', onclick: showNotes }, ['Cancel']),
      save,
    );
    updateCounter();
    textarea.focus();
  };

  showNotes();
  if (!markdown.trim() && !publication.canEditArtistMarkdown) section.hidden = true;
  section.append(el('div', { class: 'gallery-note-heading' }, [heading, controls]), body);
  return section;
};

async function renderDetail(id: string): Promise<void> {
  detailPreview?.dispose();
  detailPreview = null;
  root.replaceChildren(
    el('main', { class: 'gallery-main', id: 'gallery-main' }, [
      el('p', { class: 'gallery-muted' }, ['Loading publication…']),
    ]),
  );
  try {
    const [{ publication }, revisionResult, identity] = await Promise.all([
      request<{ publication: Publication }>(`/publications/${encodeURIComponent(id)}`),
      request<{ revisions: Publication[] }>(`/publications/${encodeURIComponent(id)}/revisions`),
      request<{
        user: { id: string } | null;
        capabilities: { cloudWrite: boolean };
      }>('/me').catch(() => ({ user: null, capabilities: { cloudWrite: false } })),
    ]);
    const revisions = revisionResult.revisions;
    let activePublication = publication;
    let vote = identity.user
      ? await request<{ upvoted: boolean; count: number }>(
          `/publications/${encodeURIComponent(id)}/upvote`,
        ).catch(() => ({ upvoted: false, count: publication.upvotes }))
      : { upvoted: false, count: publication.upvotes };

    const voteButton = el('button', {
      class: 'gallery-button gallery-upvote-button',
      type: 'button',
      onclick: () => {
        if (!identity.user) {
          window.location.assign('/documents?auth=sign-in');
          return;
        }
        voteButton.disabled = true;
        void request<{ upvoted: boolean; count: number }>(
          `/publications/${encodeURIComponent(id)}/upvote`,
          { method: vote.upvoted ? 'DELETE' : 'PUT', body: vote.upvoted ? undefined : '{}' },
        )
          .then((next) => {
            vote = next;
            updateVoteButton();
          })
          .catch((reason) => window.alert((reason as Error).message))
          .finally(() => (voteButton.disabled = false));
      },
    }) as HTMLButtonElement;
    const updateVoteButton = (): void => {
      const action = vote.upvoted ? 'Upvoted' : identity.user ? 'Upvote' : 'Sign in to upvote';
      if (identity.user) voteButton.setAttribute('aria-pressed', String(vote.upvoted));
      else voteButton.removeAttribute('aria-pressed');
      voteButton.setAttribute('aria-label', `${action}; ${vote.count} upvotes`);
      voteButton.replaceChildren(
        upvoteIcon(vote.upvoted),
        el('span', {}, [action]),
        el('span', { class: 'gallery-button-count', 'aria-hidden': 'true' }, [String(vote.count)]),
      );
    };
    updateVoteButton();

    const templateButton = el(
      'button',
      {
        class: 'gallery-button primary',
        type: 'button',
        onclick: () =>
          void (async () => {
            const revisionPath = `/publications/${encodeURIComponent(id)}/revisions/${activePublication.revision}/template`;
            if (identity.user && identity.capabilities.cloudWrite) {
              const { document } = await request<{ document: { id: string } }>(revisionPath, {
                method: 'POST',
                body: '{}',
              });
              window.location.assign(`/documents/${encodeURIComponent(document.id)}`);
              return;
            }
            const { template } = await request<{ template: { name: string; body: string } }>(
              revisionPath,
            );
            sessionStorage.setItem(LOCAL_TEMPLATE_HANDOFF_KEY, template.body);
            window.location.assign('/app?template=publication');
          })().catch((reason) => window.alert((reason as Error).message)),
      },
      ['Open as template'],
    );

    const liveHost = el(
      'div',
      {
        class: 'gallery-detail-preview gallery-live-preview',
        role: 'group',
        'aria-label':
          'Interactive material preview. Drag to orbit, shift-drag to move the light, and scroll to zoom.',
      },
      [el('span', { class: 'gallery-render-status', role: 'status' }, ['Rendering material…'])],
    );
    const title = el('h1', {}, [publication.name]);
    const byline = el('span', {}, [`Published by @${publication.publisherHandle}`]);
    const revisionControl = el('span');
    let revisionSelect: HTMLSelectElement | null = null;
    if (revisions.length > 1) {
      revisionSelect = el(
        'select',
        { class: 'gallery-revision-select', 'aria-label': 'Published revision' },
        revisions.map((revision) =>
          el('option', { value: revision.revision }, [
            `Revision ${revision.revision} · ${new Date(revision.updatedAt).toLocaleDateString()}`,
          ]),
        ),
      ) as HTMLSelectElement;
      revisionSelect.value = String(publication.revision);
      revisionControl.replaceChildren(
        el('label', { class: 'gallery-revision-control' }, [
          el('span', {}, ['Published revision']),
          revisionSelect,
        ]),
      );
    }

    const detailMain = el('main', { class: 'gallery-main', id: 'gallery-main' }, [
      el('a', { class: 'gallery-back', href: '/gallery' }, ['← All public textures']),
      el('section', { class: 'gallery-detail' }, [
        liveHost,
        el('div', { class: 'gallery-detail-copy' }, [
          title,
          el('div', { class: 'gallery-meta gallery-detail-meta' }, [
            byline,
            revisions.length > 1 ? revisionControl : false,
          ]),
          el('div', { class: 'gallery-license' }, [
            'Released under ',
            el('a', { href: publication.license.url, target: '_blank', rel: 'noreferrer' }, [
              publication.license.identifier,
            ]),
            '. You may copy, modify, and use it without restriction. Unpublishing cannot revoke copies already received.',
          ]),
          el('div', { class: 'gallery-actions' }, [templateButton, voteButton]),
        ]),
      ]),
      artistNotes(publication),
    ]);
    root.replaceChildren(detailMain);

    const initializePreview = async (): Promise<void> => {
      try {
        const controller = await createLivePreview(liveHost, activePublication);
        detailPreview = controller;
        if (activePublication.body && activePublication !== publication) {
          controller.load(activePublication.body);
        }
      } catch (reason) {
        renderPreviewFailure(
          liveHost,
          activePublication,
          `Live render unavailable: ${(reason as Error).message}`,
        );
      }
    };
    void initializePreview();

    if (revisionSelect) {
      revisionSelect.onchange = () => {
        const revision = Number(revisionSelect?.value);
        if (!Number.isSafeInteger(revision)) return;
        revisionSelect!.disabled = true;
        void request<{ publication: Publication }>(
          `/publications/${encodeURIComponent(id)}/revisions/${revision}`,
        )
          .then(({ publication: selected }) => {
            if (!selected.body) throw new Error('This revision has no renderable graph');
            if (detailPreview) detailPreview.load(selected.body);
            else if (
              liveHost
                .querySelector('.gallery-render-status')
                ?.textContent?.startsWith('Live render unavailable')
            ) {
              renderPreviewFailure(liveHost, selected, 'Live render unavailable');
            }
            activePublication = selected;
            title.textContent = selected.name;
            byline.textContent = `Published by @${selected.publisherHandle}`;
          })
          .catch((reason) => {
            revisionSelect!.value = String(activePublication.revision);
            window.alert((reason as Error).message);
          })
          .finally(() => (revisionSelect!.disabled = false));
      };
    }
  } catch (reason) {
    root.replaceChildren(
      el('main', { class: 'gallery-main', id: 'gallery-main' }, [
        el('a', { class: 'gallery-back', href: '/gallery' }, ['← Gallery']),
        el('div', { class: 'gallery-error' }, [(reason as Error).message]),
      ]),
    );
  }
}

function render(): void {
  const detail = /^\/gallery\/([A-Za-z0-9-]+)\/?$/.exec(location.pathname);
  if (detail) return;
  const selector = el('select', { class: 'gallery-sort', 'aria-label': 'Gallery order' }, [
    el('option', { value: 'top' }, ['Most upvoted']),
    el('option', { value: 'new' }, ['Newest']),
  ]) as HTMLSelectElement;
  selector.value = sort;
  selector.onchange = () => {
    sort = selector.value === 'new' ? 'new' : 'top';
    void load(true);
  };
  root.replaceChildren(
    el('main', { class: 'gallery-main', id: 'gallery-main' }, [
      el('section', { class: 'gallery-hero' }, [
        el('div', {}, [
          el('h1', {}, ['Public texture gallery']),
          el('p', {}, ['Community creations, dedicated to the public domain.']),
        ]),
        selector,
      ]),
      error
        ? el('div', { class: 'gallery-error' }, [error])
        : items.length
          ? el('div', { class: 'gallery-grid' }, items.map(card))
          : el('div', { class: 'gallery-empty' }, [
              loading ? 'Loading public textures…' : 'No textures have been published yet.',
            ]),
      nextCursor && !loading
        ? el('button', { class: 'gallery-button gallery-more', onclick: () => void load(false) }, [
            'Load more',
          ])
        : el('span'),
    ]),
  );
}

window.addEventListener('pagehide', () => {
  detailPreview?.dispose();
  detailPreview = null;
});

const detail = /^\/gallery\/([A-Za-z0-9-]+)\/?$/.exec(location.pathname);
if (detail) void renderDetail(detail[1]);
else void load(true);
